/**
 * Kalkulations-Engine fuer Strom-Vergleichsrechnung
 *
 * ANGEBOTSPREIS = Energiepreis + Provision  ->  Feld `angebotspreis_ct_kwh`.
 * Nur dieser Teil ist verhandelbar und unterscheidet ein Angebot vom anderen.
 * Danach wird sortiert, verglichen und angeboten.
 *
 * Netzentgelte, Abgaben und Umlagen werden weiterhin vollstaendig berechnet und
 * im Ergebnis mitgeliefert, aber NICHT in den Angebotspreis eingerechnet. Sie
 * sind staatlich reguliert, fuer jeden Versorger identisch und werden als
 * "zzgl. Netzentgelte, Abgaben und Umlagen" gesondert ausgewiesen.
 *
 * Kennzeichen im Ergebnis-Kopf: `preisbasis: 'energie'`. Aeltere gespeicherte
 * Kalkulationen haben dieses Feld nicht — dort galt der Komplettpreis als
 * Angebotspreis.
 */

import { getNetzbetreiber } from '@/data/netzbetreiberMapping';
import { getAbgaben, istSondervertragskunde } from '@/data/abgabenUmlagen';

import { getStrukturaufschlag, getAufschlagConfig } from '@/data/strukturaufschlag';
import { berechneNetzentgelt } from '@/data/netzentgelte';

/**
 * Allerletzte Rueckfallebene, wenn die PLZ nicht einmal einer Region zugeordnet
 * werden kann. Das ist eine grobe Schaetzung und wird als solche gekennzeichnet
 * (herkunft: 'schaetzung').
 */
const NETZENTGELT_NOTFALL_PAUSCHALE = 7.50;

/**
 * Netzentgelt fuer eine Kalkulation ermitteln.
 *
 * Reihenfolge: echtes Preisblatt des Netzbetreibers (RLM zweiteilig mit
 * Benutzungsstundenregelung, SLP mit Arbeits-/Grundpreis) — sonst die
 * Regionalpauschale aus netzbetreiberMapping als Schaetzung.
 *
 * Stichtag ist der LIEFERBEGINN: der Kunde zahlt das Netzentgelt, das waehrend
 * der Belieferung gilt. (Bei Versorgerpreisen ist der Stichtag bewusst das
 * heutige Datum — das ist ein anderer Sachverhalt.)
 */
function ermittleNetzentgelt({ netzbetreiber, spannungsebene, verbrauch, leistung, zaehlverfahren, stichtag }) {
  const pauschale = Number.isFinite(netzbetreiber?.netzentgelt)
    ? netzbetreiber.netzentgelt
    : NETZENTGELT_NOTFALL_PAUSCHALE;

  const ergebnis = berechneNetzentgelt({
    netzbetreiberName: netzbetreiber?.name || null,
    spannungsebene,
    verbrauch,
    leistung,
    zaehlverfahren,
    pauschale_ct_kwh: pauschale,
    stichtag,
  });

  // Sicherheitsnetz: die Engine rechnet mit Zahlen weiter.
  const ct = Number.isFinite(ergebnis.netzentgelt_ct_kwh) ? ergebnis.netzentgelt_ct_kwh : pauschale;
  return { ...ergebnis, netzentgelt_ct_kwh: ct };
}

/**
 * Datum als reiner Kalendertag (YYYY-MM-DD) aus den LOKALEN Bestandteilen.
 *
 * toISOString() rechnet nach UTC um: ein lokal um Mitternacht erzeugtes Datum
 * kippt oestlich von UTC auf den Vortag. Genau dadurch wurde aus einem
 * Lieferbeginn 01.01.2026 der 31.12.2025 — mit falschem Lieferjahr und damit
 * falschem Boersenpreis.
 */
const toDateOnly = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Angebotspreis eines Laufzeit-Eintrags fuer Sortierung und Vergleich.
 *
 * Regel: es wird nach dem sortiert, was auch ausgewiesen wird — dem
 * Angebotspreis (Energie + Provision). Netz und Abgaben sind fuer alle Angebote
 * identisch und aendern die Rangfolge nicht; der Code muss aber konsistent das
 * ausweisen, wonach er sortiert.
 *
 * Fallback nur fuer aeltere Objekte ohne `angebotspreis_ct_kwh`: aus Energie und
 * Provision rekonstruieren. Fehlt beides, wird Infinity zurueckgegeben — ein
 * unbekannter Preis darf niemals als "guenstigster" gewinnen (0 waere ein
 * erfundener Wert).
 */
function angebotspreisOf(laufzeit) {
  if (!laufzeit) return Infinity;
  if (Number.isFinite(laufzeit.angebotspreis_ct_kwh)) return laufzeit.angebotspreis_ct_kwh;
  if (Number.isFinite(laufzeit.energie_ct_kwh)) {
    const prov = Number.isFinite(laufzeit.provision_ct_kwh) ? laufzeit.provision_ct_kwh : 0;
    return laufzeit.energie_ct_kwh + prov;
  }
  return Infinity;
}

/**
 * Hauptfunktion: Berechnet eine vollstaendige Kalkulation
 * @param {Object} params
 * @param {string} params.plz - Postleitzahl
 * @param {number} params.verbrauch - Jahresverbrauch in kWh
 * @param {string} params.spannungsebene - NS, MS, HS
 * @param {string} params.lieferbeginn - Datum als String (YYYY-MM-DD)
 * @param {number} params.provision - Provision in ct/kWh
 * @param {Array} params.supplierPrices - Array von Versorgerpreisen
 * @param {Array} params.suppliers - Array von Versorgern
 * @returns {Object} Kalkulationsergebnis
 */
export function calculateKalkulation({
  plz,
  verbrauch,
  spannungsebene = 'NS',
  zaehlverfahren = 'SLP',
  leistung = 0,
  lieferbeginn,
  provision = 1.0,
  supplierPrices = [],
  suppliers = [],
  companyId = null,
  netzbetreiberName = null,
}) {
  // 1. Netzentgelt ermitteln (Preisblatt falls vorhanden, sonst Regionalpauschale)
  const erkannt = getNetzbetreiber(plz, spannungsebene);
  // Bei PLZ mit mehreren Netzbetreibern zaehlt die Auswahl des Nutzers —
  // sonst wuerde das Preisblatt eines fremden Betreibers herangezogen.
  const netzbetreiber = netzbetreiberName
    ? { ...erkannt, name: netzbetreiberName }
    : erkannt;
  const netzErgebnis = ermittleNetzentgelt({
    netzbetreiber,
    spannungsebene,
    verbrauch,
    leistung,
    zaehlverfahren,
    stichtag: lieferbeginn,
  });
  const netzentgelt = netzErgebnis.netzentgelt_ct_kwh;

  // 2. Abgaben ermitteln (Konzessionsabgabe abhaengig von Tarif-/Sondervertragskunde)
  const isSondervertrag = istSondervertragskunde({ verbrauch, zaehlverfahren, leistung });
  const abgabenData = getAbgaben(plz, spannungsebene, isSondervertrag);
  const abgaben = abgabenData.gesamt;

  // 3. Lieferbeginn-Datum parsen
  const startDate = lieferbeginn ? new Date(lieferbeginn) : new Date();

  // 4. Preise filtern: Verbrauchsband, Gueltigkeit, Kundenbindung.
  // Stichtag ist BEWUSST das heutige Datum (Angebotszeitpunkt), nicht der
  // Lieferbeginn — Versorger-Preisblaetter gelten fuer die Angebotsabgabe.
  const filteredPrices = filterPricesByConsumption(supplierPrices, verbrauch, { companyId });

  // 5. Nach Versorger gruppieren
  const supplierMap = new Map();
  suppliers.forEach(s => supplierMap.set(s.id, s));

  const grouped = groupBySupplier(filteredPrices, supplierMap);

  // 6. Fuer jeden Versorger + Laufzeit berechnen
  const results = [];
  const laufzeitenSet = new Set();

  grouped.forEach((supplierData) => {
    const laufzeiten = [];

    supplierData.prices.forEach((price) => {
      const durationMonths = price.contract_duration_months || 12;
      const lieferende = calculateLieferende(startDate, durationMonths);
      const laufzeitKey = durationMonths;

      laufzeitenSet.add(laufzeitKey);

      const energiePreis = price.price_ct_kwh;
      // ANGEBOTSPREIS: nur der verhandelbare Teil (Energie + Provision).
      const angebotspreis = energiePreis + provision;
      const angebotspreisJahr = (angebotspreis / 100) * verbrauch;
      // Interne Gesamtkosten-Abschaetzung inkl. Netz und Abgaben — NICHT der
      // Angebotspreis (siehe Kopfkommentar der Datei).
      const komplettpreis = energiePreis + netzentgelt + abgaben + provision;
      const komplettpreisJahr = (komplettpreis / 100) * verbrauch;
      const energieKostenJahr = (energiePreis / 100) * verbrauch;
      const provisionJahr = (provision / 100) * verbrauch;

      laufzeiten.push({
        laufzeit_monate: laufzeitKey,
        lieferende: toDateOnly(lieferende),
        lieferende_display: formatDateDE(lieferende),
        energie_ct_kwh: round4(energiePreis),
        netzentgelt_ct_kwh: round4(netzentgelt),
        // Kennzeichnung mitfuehren, damit auch PDF/Freigabe-Ansicht (die nur
        // cheapestPerLaufzeit sehen) Schaetzung von Preisblatt unterscheiden.
        netzentgelt_herkunft: netzErgebnis.herkunft,
        abgaben_ct_kwh: round4(abgaben),
        provision_ct_kwh: round4(provision),
        // ANGEBOTSPREIS = Energie + Provision. Das ist der Preis, der dem Kunden
        // genannt wird ("zzgl. Netzentgelte, Abgaben und Umlagen").
        angebotspreis_ct_kwh: round4(angebotspreis),
        angebotspreis_jahr_eur: round2(angebotspreisJahr),
        // NUR INTERN: Gesamtkosten-Abschaetzung inkl. Netzentgelt und Abgaben.
        // Nicht als Angebotspreis anzeigen und nicht danach sortieren.
        komplettpreis_ct_kwh: round4(komplettpreis),
        komplettpreis_jahr_eur: round2(komplettpreisJahr),
        energie_kosten_jahr_eur: round2(energieKostenJahr),
        provision_jahr_eur: round2(provisionJahr),
        price_type: price.price_type || 'fixed',
        price_id: price.id,
        supplier_price_id: price.id,
      });
    });

    // Sortiere Laufzeiten
    laufzeiten.sort((a, b) => a.laufzeit_monate - b.laufzeit_monate);

    results.push({
      supplier_id: supplierData.supplier.id,
      supplier_name: supplierData.supplier.name,
      supplier: supplierData.supplier,
      laufzeiten,
    });
  });

  // 7. Guenstigstes Angebot pro Laufzeit finden
  const laufzeiten = Array.from(laufzeitenSet).sort((a, b) => a - b);
  const cheapestPerLaufzeit = findCheapestPerLaufzeit(results, laufzeiten);

  // 8. Ergebnisse sortieren (nach guenstigstem ANGEBOTSPREIS = Energie + Provision).
  // Frueher wurde hier nach energie_ct_kwh sortiert, der Kommentar sprach aber
  // vom Komplettpreis — beides ist jetzt vereinheitlicht auf den Angebotspreis.
  results.sort((a, b) => {
    const aMin = Math.min(...a.laufzeiten.map(angebotspreisOf));
    const bMin = Math.min(...b.laufzeiten.map(angebotspreisOf));
    return aMin - bMin;
  });

  return {
    // Meta
    plz,
    verbrauch,
    spannungsebene,
    zaehlverfahren, // wurde frueher nicht zurueckgegeben -> Ausschreibung speicherte NULL
    leistung,
    lieferbeginn: toDateOnly(startDate),
    provision,

    // Kennzeichen der Preislogik: Angebotspreis = Energie + Provision,
    // Netzentgelte/Abgaben werden nur zzgl. ausgewiesen. Fehlt das Feld,
    // stammt die Kalkulation aus der alten Komplettpreis-Logik.
    preisbasis: 'energie',

    // Netz & Abgaben (weiterhin voll berechnet, aber NICHT im Angebotspreis)
    netzbetreiber: netzbetreiber?.name || 'Unbekannt',
    netzentgelt_ct_kwh: round4(netzentgelt),
    // Herkunft des Netzentgelts — 'preisblatt' = echter Tarif dieses
    // Netzbetreibers, 'schaetzung' = Regionalpauschale nach PLZ-Region.
    netzentgelt_herkunft: netzErgebnis.herkunft,
    netzentgelt_quelle: netzErgebnis.quelle,
    netzentgelt_hinweis: netzErgebnis.hinweis,
    netzentgelt_leistungspreis_anteil_ct_kwh: netzErgebnis.leistungspreis_anteil_ct_kwh,
    benutzungsstunden: netzErgebnis.benutzungsstunden,
    netzentgelt_detail: netzErgebnis,
    abgaben_ct_kwh: round4(abgaben),
    abgaben_detail: abgabenData.einzeln,

    // Ergebnisse
    results,
    laufzeiten,
    cheapestPerLaufzeit,

    // Zusammenfassung
    total_suppliers: results.length,
    total_angebote: results.reduce((sum, r) => sum + r.laufzeiten.length, 0),
  };
}

/**
 * Filtert Preise nach Verbrauchsband, Gueltigkeit und Kundenbindung.
 *
 * Frueher wurde NUR das Verbrauchsband geprueft. Dadurch flossen zwei Arten von
 * Preisen in jede Kalkulation, die dort nicht hingehoeren:
 *  - kundenspezifische Sondertarife (company_id gesetzt) — sie gewannen als
 *    guenstigster Preis den Vergleich auch bei voellig fremden Kunden,
 *  - abgelaufene Preise (valid_until in der Vergangenheit).
 *
 * @param {Array} prices
 * @param {number} verbrauch
 * @param {object} [opts]
 * @param {string|number|null} [opts.companyId] - nur Sondertarife dieser Firma zulassen
 * @param {Date} [opts.stichtag] - Bezugsdatum der Gueltigkeitspruefung (Default: heute)
 */
function filterPricesByConsumption(prices, verbrauch, opts = {}) {
  const { companyId = null, stichtag = new Date() } = opts;
  const tag = stichtag instanceof Date ? stichtag : new Date(stichtag);

  return prices.filter(price => {
    if (price.is_active === false) return false;

    // Verbrauchsband
    const min = price.min_consumption_kwh || 0;
    const max = price.max_consumption_kwh || Infinity;
    if (verbrauch < min || verbrauch > max) return false;

    // Kundenbindung: Preise mit company_id gelten NUR fuer diese Firma.
    // Ohne bekannte Firma zaehlen ausschliesslich allgemeine Preise.
    if (price.company_id != null && price.company_id !== '') {
      if (companyId == null || String(price.company_id) !== String(companyId)) return false;
    }

    // Gueltigkeitszeitraum
    if (price.valid_from && new Date(price.valid_from) > tag) return false;
    if (price.valid_until && new Date(price.valid_until) < tag) return false;

    return true;
  });
}

/**
 * Gruppiert Preise nach Versorger
 */
function groupBySupplier(prices, supplierMap) {
  const groups = new Map();

  prices.forEach(price => {
    const supplierId = price.supplier_id;
    if (!groups.has(supplierId)) {
      const supplier = supplierMap.get(supplierId) || {
        id: supplierId,
        name: `Versorger ${supplierId}`,
      };
      groups.set(supplierId, {
        supplier,
        prices: [],
      });
    }
    groups.get(supplierId).prices.push(price);
  });

  return groups;
}

/**
 * Berechnet Lieferende basierend auf Lieferbeginn + Laufzeit.
 *
 * Frueher wurde auf den 31.12. des Endjahres aufgerundet — dadurch wurde aus
 * einem 12-Monats-Vertrag ab Juli faktisch ein 18-Monats-Vertrag, waehrend der
 * Preis weiter fuer 12 Monate galt. Jetzt gilt die tatsaechliche Laufzeit.
 */
function calculateLieferende(startDate, durationMonths) {
  const end = new Date(startDate);
  end.setMonth(end.getMonth() + durationMonths);
  end.setDate(end.getDate() - 1); // Vertragsende = Tag vor dem Jahrestag
  return end;
}

/**
 * Tatsaechliche Laufzeit in Monaten zwischen zwei Daten (kaufmaennisch gerundet).
 */
function monthsBetween(start, end) {
  const months = (end.getFullYear() - start.getFullYear()) * 12
    + (end.getMonth() - start.getMonth())
    + (end.getDate() >= start.getDate() ? 0 : -1);
  return Math.max(1, months + 1);
}

/**
 * Findet das guenstigste Angebot pro Laufzeit — entschieden nach
 * ANGEBOTSPREIS (Energie + Provision), nicht nach Komplettpreis.
 */
function findCheapestPerLaufzeit(results, laufzeiten) {
  const cheapest = {};

  laufzeiten.forEach(lz => {
    let best = null;

    results.forEach(result => {
      const match = result.laufzeiten.find(l => l.laufzeit_monate === lz);
      if (match && (!best || angebotspreisOf(match) < angebotspreisOf(best))) {
        best = {
          ...match,
          supplier_id: result.supplier_id,
          supplier_name: result.supplier_name,
        };
      }
    });

    if (best) {
      cheapest[lz] = best;
    }
  });

  return cheapest;
}

/**
 * Zeilen der Vergleichstabelle fuer EIN Angebot (eine Spalte der Matrix).
 *
 * Hier stand frueher eine Zeile "Komplettpreis". Sie ist bewusst durch
 * "Angebotspreis (Energie)" ersetzt — der Kunde darf nie den Eindruck bekommen,
 * die genannte Zahl sei sein Gesamtpreis. Netzentgelte und Abgaben bleiben als
 * eigene, ausdruecklich mit "zzgl." markierte Zeilen erhalten, damit der Makler
 * die Groessenordnung weiterhin sieht.
 *
 * kategorie: 'angebot' = Teil des Angebotspreises,
 *            'detail'  = Aufschluesselung darunter,
 *            'zzgl'    = reguliert, NICHT im Angebotspreis enthalten.
 */
function buildAngebotZeilen(angebot) {
  if (!angebot) return [];

  const zeilen = [
    { key: 'energie_ct_kwh', label: 'Energiepreis', einheit: 'ct/kWh', wert: angebot.energie_ct_kwh, kategorie: 'angebot' },
  ];

  // Boersenmodus: Beschaffung und Strukturaufschlag getrennt zeigen.
  if (angebot.boerse_ct_kwh != null) {
    zeilen.push({ key: 'boerse_ct_kwh', label: 'davon Boerse (EEX)', einheit: 'ct/kWh', wert: angebot.boerse_ct_kwh, kategorie: 'detail' });
    zeilen.push({ key: 'aufschlag_ct_kwh', label: 'davon Strukturaufschlag', einheit: 'ct/kWh', wert: angebot.aufschlag_ct_kwh, kategorie: 'detail' });
  }

  zeilen.push({ key: 'provision_ct_kwh', label: 'AIQCON-Provision', einheit: 'ct/kWh', wert: angebot.provision_ct_kwh, kategorie: 'angebot' });
  zeilen.push({ key: 'angebotspreis_ct_kwh', label: 'Angebotspreis (Energie)', einheit: 'ct/kWh', wert: angebotspreisOf(angebot), kategorie: 'angebot', hervorheben: true });
  zeilen.push({ key: 'angebotspreis_jahr_eur', label: 'Angebotspreis (Energie) pro Jahr', einheit: 'EUR/a', wert: angebot.angebotspreis_jahr_eur, kategorie: 'angebot' });
  zeilen.push({ key: 'netzentgelt_ct_kwh', label: 'zzgl. Netzentgelte', einheit: 'ct/kWh', wert: angebot.netzentgelt_ct_kwh, kategorie: 'zzgl', herkunft: angebot.netzentgelt_herkunft });
  zeilen.push({ key: 'abgaben_ct_kwh', label: 'zzgl. Abgaben und Umlagen', einheit: 'ct/kWh', wert: angebot.abgaben_ct_kwh, kategorie: 'zzgl' });

  return zeilen;
}

/**
 * Berechnet die Vergleichstabelle (wie ESOS24 Aufschlag-Ansicht)
 * Gibt eine Matrix zurueck: guenstigstes Angebot pro Laufzeit.
 *
 * Sortiert und entschieden wird nach ANGEBOTSPREIS (Energie + Provision).
 */
export function buildComparisonTable(kalkulationResult) {
  const { results, laufzeiten } = kalkulationResult;

  // Fuer jede Laufzeit: guenstigstes Angebot
  const table = laufzeiten.map(lz => {
    const candidates = [];

    results.forEach(result => {
      const match = result.laufzeiten.find(l => l.laufzeit_monate === lz);
      if (match) {
        candidates.push({
          ...match,
          supplier_id: result.supplier_id,
          supplier_name: result.supplier_name,
        });
      }
    });

    // Sortiere nach Angebotspreis (Energie + Provision) — nicht nach
    // Komplettpreis und nicht nach reinem Energiepreis.
    candidates.sort((a, b) => angebotspreisOf(a) - angebotspreisOf(b));

    const cheapest = candidates[0] || null;

    return {
      laufzeit_monate: lz,
      cheapest,
      all: candidates,
      // Beschriftete Zeilen der Spalte: Angebotspreis (Energie) statt
      // Komplettpreis, Netz und Abgaben als "zzgl."-Zeilen.
      zeilen: buildAngebotZeilen(cheapest),
    };
  });

  return table;
}

/**
 * Boersenbasiertes Universalangebot.
 * Energiepreis = gemischter EEX-Baseload (Cal-Jahre der Laufzeit) + Strukturaufschlag.
 * Gibt EIN Angebot pro Laufzeit zurueck (AIQCON-Branding, kein Versorger-Loop).
 *
 * @param {Object} params
 * @param {string} params.plz
 * @param {number} params.verbrauch - kWh/a
 * @param {string} params.zaehlverfahren - 'SLP' | 'RLM'
 * @param {string} params.spannungsebene
 * @param {string} params.lieferbeginn - YYYY-MM-DD
 * @param {number} params.provision - ct/kWh
 * @param {Object} params.futures - { "2027": 9.45, ... } in ct/kWh (von /api/futures)
 * @param {number[]} [params.laufzeitenJahre] - z.B. [1,2,3,4,5]
 * @returns {Object} Ergebnis kompatibel zur calculateKalkulation-Struktur
 */
export function calculateUniversalOffer({
  plz,
  verbrauch,
  zaehlverfahren = 'SLP',
  spannungsebene = 'NS',
  leistung = 0,
  lieferbeginn,
  provision = 1.0,
  futures = {},
  laufzeitenJahre = [1, 2, 3, 4, 5],
  netzbetreiberName = null,
}) {
  // Netz & Abgaben (wie bestehende Engine)
  const erkannt = getNetzbetreiber(plz, spannungsebene);
  // Bei PLZ mit mehreren Netzbetreibern zaehlt die Auswahl des Nutzers —
  // sonst wuerde das Preisblatt eines fremden Betreibers herangezogen.
  const netzbetreiber = netzbetreiberName
    ? { ...erkannt, name: netzbetreiberName }
    : erkannt;
  const netzErgebnis = ermittleNetzentgelt({
    netzbetreiber,
    spannungsebene,
    verbrauch,
    leistung,
    zaehlverfahren,
    stichtag: lieferbeginn,
  });
  const netzentgelt = netzErgebnis.netzentgelt_ct_kwh;
  // Konzessionsabgabe abhaengig von Tarif-/Sondervertragskunde
  const isSondervertrag = istSondervertragskunde({ verbrauch, zaehlverfahren, leistung });
  const abgabenData = getAbgaben(plz, spannungsebene, isSondervertrag);
  const abgaben = abgabenData.gesamt;

  const startDate = lieferbeginn ? new Date(lieferbeginn) : new Date();
  // Lieferbeginn auf 1. des Folgemonats falls kein Datum -> Startjahr
  const startYear = startDate.getFullYear();

  const aufschlagConfig = getAufschlagConfig();

  const laufzeiten = [];
  const laufzeitMonate = [];

  laufzeitenJahre.forEach((jahre) => {
    const endYear = startYear + jahre - 1;

    // Gemischter Baseload: Durchschnitt der Cal-Jahre-Futures ueber die Lieferperiode
    const deliveryYears = [];
    for (let y = startYear; y <= endYear; y++) deliveryYears.push(y);
    const baseValues = deliveryYears
      .map((y) => futures[String(y)])
      .filter((v) => v != null && !isNaN(v));

    // Fallback: wenn ein Jahr fehlt, letzten bekannten Wert nutzen
    let blendedBase;
    if (baseValues.length > 0) {
      blendedBase = baseValues.reduce((s, v) => s + v, 0) / baseValues.length;
    } else {
      // letzter verfuegbarer Future-Wert
      const allYears = Object.keys(futures).sort();
      // KEIN erfundener Rueckfallwert. Hier stand frueher pauschal 8,0 ct/kWh —
      // diese Zahl waere nach der Umstellung direkt der Angebotspreis geworden,
      // ohne dass irgendwo erkennbar gewesen waere, dass sie geraten ist.
      blendedBase = allYears.length ? futures[allYears[allYears.length - 1]] : null;
    }

    if (!Number.isFinite(blendedBase)) {
      // Ohne Boersendaten gibt es keinen Preis. Die Laufzeit wird uebersprungen;
      // die Oberflaeche zeigt dann den vorhandenen Hinweis auf fehlende Futures.
      return;
    }

    const aufschlag = getStrukturaufschlag(jahre, verbrauch, zaehlverfahren, aufschlagConfig);
    const energie = blendedBase + aufschlag;

    // Lieferende zum Jahresende (Kalenderjahresvertrag, marktueblich).
    // Die ausgewiesene Laufzeit ist die TATSAECHLICHE Dauer ab Lieferbeginn —
    // frueher stand hier pauschal jahre*12, obwohl ein Vertrag ab Juni bis
    // 31.12. nur rund ein halbes Jahr laeuft.
    const lieferende = new Date(endYear, 11, 31);
    const monate = monthsBetween(startDate, lieferende);

    // ANGEBOTSPREIS: Energie (Boerse + Strukturaufschlag, siehe oben) + Provision.
    // Der Strukturaufschlag steckt bereits in `energie` — nicht doppelt addieren.
    const angebotspreis = energie + provision;
    const angebotspreisJahr = (angebotspreis / 100) * verbrauch;
    // Interne Gesamtkosten-Abschaetzung inkl. Netz und Abgaben — NICHT der
    // Angebotspreis (siehe Kopfkommentar der Datei).
    const komplettpreis = energie + netzentgelt + abgaben + provision;
    const komplettpreisJahr = (komplettpreis / 100) * verbrauch;
    const energieKostenJahr = (energie / 100) * verbrauch;
    const provisionJahr = (provision / 100) * verbrauch;

    laufzeitMonate.push(monate);
    laufzeiten.push({
      laufzeit_monate: monate,
      laufzeit_jahre: jahre,
      lieferende: toDateOnly(lieferende),
      lieferende_display: formatDateDE(lieferende),
      // Preis-Komponenten
      boerse_ct_kwh: round4(blendedBase),
      aufschlag_ct_kwh: round4(aufschlag),
      energie_ct_kwh: round4(energie),
      netzentgelt_ct_kwh: round4(netzentgelt),
      netzentgelt_herkunft: netzErgebnis.herkunft,
      abgaben_ct_kwh: round4(abgaben),
      provision_ct_kwh: round4(provision),
      // ANGEBOTSPREIS = Energie + Provision. Das ist der Preis, der dem Kunden
      // genannt wird ("zzgl. Netzentgelte, Abgaben und Umlagen").
      angebotspreis_ct_kwh: round4(angebotspreis),
      angebotspreis_jahr_eur: round2(angebotspreisJahr),
      // NUR INTERN: Gesamtkosten-Abschaetzung inkl. Netzentgelt und Abgaben.
      // Nicht als Angebotspreis anzeigen und nicht danach sortieren.
      komplettpreis_ct_kwh: round4(komplettpreis),
      komplettpreis_jahr_eur: round2(komplettpreisJahr),
      energie_kosten_jahr_eur: round2(energieKostenJahr),
      provision_jahr_eur: round2(provisionJahr),
      price_type: 'fixed',
    });
  });

  // EIN "Versorger" = AIQCON Universal
  const results = [{
    supplier_id: 'aiqcon-universal',
    supplier_name: 'AIQCON Universaltarif',
    supplier: { id: 'aiqcon-universal', name: 'AIQCON Universaltarif' },
    laufzeiten,
  }];

  // cheapestPerLaufzeit = das einzige Angebot je Laufzeit
  const cheapestPerLaufzeit = {};
  laufzeiten.forEach((lz) => {
    cheapestPerLaufzeit[lz.laufzeit_monate] = {
      ...lz,
      supplier_id: 'aiqcon-universal',
      supplier_name: 'AIQCON Universaltarif',
    };
  });

  return {
    mode: 'boerse',
    plz,
    verbrauch,
    zaehlverfahren,
    spannungsebene,
    leistung,
    lieferbeginn: toDateOnly(startDate),
    provision,

    // Kennzeichen der Preislogik: Angebotspreis = Energie + Provision,
    // Netzentgelte/Abgaben werden nur zzgl. ausgewiesen. Fehlt das Feld,
    // stammt die Kalkulation aus der alten Komplettpreis-Logik.
    preisbasis: 'energie',

    // Netz & Abgaben (weiterhin voll berechnet, aber NICHT im Angebotspreis)
    netzbetreiber: netzbetreiber?.name || 'Unbekannt',
    netzentgelt_ct_kwh: round4(netzentgelt),
    netzentgelt_herkunft: netzErgebnis.herkunft,
    netzentgelt_quelle: netzErgebnis.quelle,
    netzentgelt_hinweis: netzErgebnis.hinweis,
    netzentgelt_leistungspreis_anteil_ct_kwh: netzErgebnis.leistungspreis_anteil_ct_kwh,
    benutzungsstunden: netzErgebnis.benutzungsstunden,
    netzentgelt_detail: netzErgebnis,
    abgaben_ct_kwh: round4(abgaben),
    abgaben_detail: abgabenData.einzeln,

    results,
    laufzeiten: laufzeitMonate,
    cheapestPerLaufzeit,

    total_suppliers: 1,
    total_angebote: laufzeiten.length,
  };
}

/**
 * Generiert eine Kalkulationsnummer
 */
export function generateKalkulationNumber() {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `KAL-${year}-${random}`;
}

// Hilfsfunktionen
function round4(n) {
  return Math.round(n * 10000) / 10000;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function formatDateDE(date) {
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
}
